import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { saveBlobResponse } from '../utils/download';
import { hasAnyRole } from '../utils/permissionUtils';
import { isOrgAdminRole } from '../utils/roleUtils';
import { MAX_SLIP_FILES, prepareSlipFile, uploadSlipFiles } from '../utils/slipFiles';
import ItemDescriptionInput, { mergeGrnLines } from './ItemDescriptionInput';
import { lineEdit, rateFromAmount } from '../utils/lineAmounts';
import GoodsReceiptChoice, { GoodsStatusBadge, GoodsToArriveButton, emptyGoodsReceipt, goodsReceiptProblem } from './GoodsReceiptChoice';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
// A unit price in full: paise, and the further decimals a vendor's rate has (up to four)
const rateText = (n) => {
  const r = Number(n.toFixed(4));
  return r.toFixed(Math.max(2, (String(r).split('.')[1] || '').length));
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const emptyLine = () => ({
  lineType: 'GOODS', description: '', itemId: null, unitOfMeasurementId: null, unitSymbol: '', quantity: '', unitPrice: '', driver: 'taxable', amount: '', expenseLedgerId: ''
});
/** A GRN line as a goods line of the slip: the received item, quantity and value. */
const grnLine = (gl) => ({
  ...emptyLine(),
  description: gl.description || '', itemId: gl.itemId, quantity: gl.quantity ?? '',
  unitOfMeasurementId: gl.unitOfMeasurementId, unitSymbol: gl.unitSymbol || '', amount: gl.amount ?? '', unitPrice: rateFromAmount(gl.quantity, gl.amount)
});
const TYPE_LABEL = { GOODS: 'Goods', SERVICE: 'Service', EXPENSE: 'Expense' };

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

/**
 * Purchases with no GST: a "kachchi parchi" slip, or a bill from an unregistered vendor, for goods, services or expenses.
 * No tax and no input credit; the vendor is owed the amount, goods already received on a GRN are not booked twice, and the
 * accountant gets a period list to report them (GSTR-3B shows them as non-GST inward supplies).
 */
function NonGstPurchases({ active }) {
  const { user } = useAuth();
  const [slips, setSlips] = useState([]);
  const [photoCounts, setPhotoCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    axios.get('/api/purchase-invoices')
      .then((r) => { if (!cancelled) setSlips((Array.isArray(r.data) ? r.data : []).filter((i) => i.nonGst)); })
      .catch(() => { if (!cancelled) toast.error('Error fetching non-GST purchases'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get('/api/purchase-invoices/attachment-counts')
      .then((r) => { if (!cancelled) setPhotoCounts(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active, reloadKey]);

  useEffect(() => {
    if (!active || !user?.organizationId) return;
    axios.get(`/api/organizations/${user.organizationId}`)
      .then((r) => setCurrency(r.data?.defaultCurrency || ''))
      .catch(() => {});
  }, [active, user?.organizationId]);

  const money = (v) => `${currency} ${num(v).toFixed(2)}`;
  const shown = useMemo(() => slips.filter((s) => (!from || s.bookingDate >= from) && (!to || s.bookingDate <= to)), [slips, from, to]);
  const recordedTotal = shown.filter((s) => s.status === 'POSTED').reduce((sum, s) => sum + num(s.totalAmount), 0);

  const download = async () => {
    try {
      setDownloading(true);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const response = await axios.get('/api/purchase-invoices/non-gst/export', { params, responseType: 'blob' });
      saveBlobResponse(response, 'non-gst-purchases.csv');
    } catch (error) {
      toast.error('Could not download the list');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Record goods, services and expenses you bought with no GST: a slip (&quot;kachchi parchi&quot;) or a bill from a vendor who is not registered.
          There is no tax and no input credit. For a normal GST bill use Purchase Invoices.
        </p>
        <Button variant="primary" onClick={() => setShowForm(true)}>+ Record non-GST purchase</Button>
      </div>

      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <Form.Group>
          <Form.Label className="mb-1">Booked from</Form.Label>
          <DateInput value={from} onChange={(e) => setFrom(e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label className="mb-1">to</Form.Label>
          <DateInput value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </Form.Group>
        <Button variant="outline-secondary" onClick={download} disabled={downloading}>
          {downloading ? 'Preparing...' : 'Download list for your CA (CSV)'}
        </Button>
        <div className="ms-auto text-end">
          <div className="text-muted small">Recorded in this view</div>
          <strong>{money(recordedTotal)}</strong>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center"><Spinner animation="border" role="status" /></div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th title="iBigDo's own number for this booking">Our ref</th><th title="The number on the vendor's slip, if it has one">Vendor's slip no.</th><th>Vendor</th><th>Slip date</th><th>Booked</th><th>What</th>
                  <th className="text-end">Amount</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => {
                  const kinds = [...new Set((s.lines || []).map((l) => l.lineType))];
                  return (
                    <tr key={s.id}>
                      <td><strong>{s.internalNumber}</strong></td>
                      <td>
                        {s.vendorInvoiceNumber || '-'}
                        {photoCounts[s.id] > 0 && <Badge bg="light" text="dark" className="ms-1 border" title="Photos of the slip">&#128206; {photoCounts[s.id]}</Badge>}
                      </td>
                      <td>{s.vendorName}</td>
                      <td>{formatDate(s.invoiceDate)}</td>
                      <td>{formatDate(s.bookingDate)}</td>
                      <td>{kinds.map((k) => <Badge key={k} bg="light" text="dark" className="me-1 border">{TYPE_LABEL[k] || k}</Badge>)}</td>
                      <td className="text-end">{money(s.totalAmount)}</td>
                      <td><Badge bg={s.status === 'POSTED' ? 'success' : 'secondary'}>{s.status === 'POSTED' ? 'Recorded' : 'Cancelled'}</Badge>{s.status === 'POSTED' && <GoodsStatusBadge status={s.goodsStatus} />}</td>
                      <td className="text-nowrap"><Button size="sm" variant="outline-info" onClick={() => setViewing(s)}>View</Button><HistoryButton documentType="PurchaseInvoice" documentId={s.id} label={`Slip ${s.internalNumber}`} className="ms-1" /></td>
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr><td colSpan="9" className="text-center text-muted">No non-GST purchases recorded{from || to ? ' in this period' : ' yet'}.</td></tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <SlipForm show={showForm} currency={currency} onHide={() => setShowForm(false)} onSaved={() => setReloadKey((k) => k + 1)} />
      <SlipDetail
        slip={viewing}
        money={money}
        currency={currency}
        onHide={() => setViewing(null)}
        onChanged={() => { setViewing(null); setReloadKey((k) => k + 1); }}
      />
    </div>
  );
}

function SlipForm({ show, currency, onHide, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [grns, setGrns] = useState([]);
  const [selectedGrns, setSelectedGrns] = useState([]);
  const [slipNumber, setSlipNumber] = useState('');
  const [slipDate, setSlipDate] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [photos, setPhotos] = useState([]);
  const [items, setItems] = useState([]);
  const [goodsReceipt, setGoodsReceipt] = useState(emptyGoodsReceipt());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setVendorId(''); setGrns([]); setSelectedGrns([]); setSlipNumber('');
    setSlipDate(''); setBookingDate(''); setNotes(''); setLines([emptyLine()]); setPhotos([]);
    setGoodsReceipt(emptyGoodsReceipt());
    axios.get('/api/inventory/vendors/active')
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Could not load vendors'));
    axios.get('/api/purchase-invoices/expense-ledgers')
      .then((r) => setLedgers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLedgers([]));
    axios.get('/api/purchase-invoices/line-items')
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, [show]);

  useEffect(() => {
    setSelectedGrns([]);
    setLines((prev) => mergeGrnLines(prev, [], [], grnLine, emptyLine));
    if (!vendorId) { setGrns([]); return; }
    axios.get(`/api/purchase-invoices/vendor/${vendorId}/uninvoiced-grns`)
      .then((r) => setGrns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setGrns([]));
  }, [vendorId]);

  const vendor = vendors.find((v) => String(v.id) === String(vendorId));
  const grnValue = grns.filter((g) => selectedGrns.includes(g.id)).reduce((s, g) => s + num(g.value), 0);
  const total = lines.reduce((s, l) => s + num(l.amount), 0);
  const goodsTotal = lines.filter((l) => l.lineType === 'GOODS').reduce((s, l) => s + num(l.amount), 0);
  const difference = round2(goodsTotal - grnValue);

  const setLine = (idx, patch) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  // Quantity, unit price and amount work together: typing one fills in the others (see utils/lineAmounts)
  const editAmount = (idx, field, value) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...lineEdit(l, field, value, 'amount') } : l)));
  const toggleGrn = (id) => {
    const next = selectedGrns.includes(id) ? selectedGrns.filter((x) => x !== id) : [...selectedGrns, id];
    setSelectedGrns(next);
    setLines((prev) => mergeGrnLines(prev, grns, next, grnLine, emptyLine));
  };

  const addPhotos = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    let room = MAX_SLIP_FILES - photos.length;
    for (const f of picked) {
      if (room <= 0) { toast.error(`${MAX_SLIP_FILES} photos at most`); break; }
      try {
        const ready = await prepareSlipFile(f);
        setPhotos((prev) => [...prev, ready]);
        room -= 1;
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  // Goods with no GRN linked: the slip says where they are (see GoodsReceiptChoice); services and expenses never have one
  const goodsLines = lines.filter((l) => l.lineType === 'GOODS' && (l.description.trim() || l.amount !== ''));
  const goodsWithoutGrn = selectedGrns.length === 0 && goodsLines.length > 0;

  const submit = async () => {
    if (!vendorId) { toast.error('Choose the vendor'); return; }
    const goodsProblem = goodsWithoutGrn && goodsReceiptProblem(goodsReceipt, goodsLines);
    if (goodsProblem) { toast.error(goodsProblem); return; }
    try {
      setSaving(true);
      const payload = {
        vendorId: Number(vendorId),
        slipNumber: slipNumber.trim() || null,
        slipDate: slipDate || null,
        bookingDate: bookingDate || null,
        dueDate: dueDate || null,
        notes,
        grnIds: selectedGrns,
        goodsReceipt: goodsWithoutGrn ? goodsReceipt : null,
        lines: lines.map((l) => ({
          description: l.description,
          lineType: l.lineType,
          itemId: l.lineType === 'GOODS' && l.itemId ? l.itemId : null,
          unitOfMeasurementId: l.lineType === 'GOODS' && l.unitOfMeasurementId ? l.unitOfMeasurementId : null,
          quantity: l.lineType === 'GOODS' && l.quantity !== '' ? num(l.quantity) : null,
          amount: l.amount === '' ? null : num(l.amount),
          expenseLedgerId: l.lineType !== 'GOODS' && l.expenseLedgerId ? Number(l.expenseLedgerId) : null
        }))
      };
      const response = await axios.post('/api/purchase-invoices/non-gst', payload);
      const failed = photos.length > 0
        ? await uploadSlipFiles(axios, response.data.id, photos, (m) => toast.error(m))
        : 0;
      if (failed > 0) {
        toast.warn(`Recorded as ${response.data.internalNumber}, but ${failed} photo${failed > 1 ? 's' : ''} did not upload. Add ${failed > 1 ? 'them' : 'it'} from the purchase's details.`);
      } else {
        toast.success(`Recorded as ${response.data.internalNumber}`);
      }
      onSaved();
      onHide();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to record the purchase'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton><Modal.Title>Record non-GST purchase</Modal.Title></Modal.Header>
      <Modal.Body>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Vendor</Form.Label>
              <Form.Select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">-- Choose --</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Form.Select>
              {vendor && vendor.taxId && (
                <Form.Text className="text-warning d-block">
                  This vendor is GST-registered, so a GST invoice is normally expected. Use this only if they really did not charge GST
                  (for example exempt or nil-rated goods).
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Slip number (optional)</Form.Label>
              <Form.Control value={slipNumber} maxLength={40} onChange={(e) => setSlipNumber(e.target.value)} />
              <Form.Text className="text-muted">Leave blank if the slip has none.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Slip date (blank = booking day)</Form.Label>
              <DateInput value={slipDate} onChange={(e) => setSlipDate(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Booking date (blank = today)</Form.Label>
              <DateInput value={bookingDate} min={slipDate} onChange={(e) => setBookingDate(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Due date</Form.Label>
              <DateInput value={dueDate} min={slipDate || undefined} onChange={(e) => setDueDate(e.target.value)} />
              <Form.Text className="text-muted">
                {(() => {
                  const v = vendors.find((x) => String(x.id) === String(vendorId));
                  if (!v) return 'Blank: worked out from the vendor\u2019s credit days.';
                  const msme = v.msmeCategory === 'MICRO' || v.msmeCategory === 'SMALL';
                  const days = msme ? Math.min(v.creditDays ?? 15, 45) : (v.creditDays ?? 0);
                  return `Blank: ${days} day${days === 1 ? '' : 's'} after the slip date${msme ? ' (MSME: 45 days at most)' : ''}.`;
                })()}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {vendorId && grns.length > 0 && (
          <div className="mb-3">
            <Form.Label>Goods this slip covers (received GRNs not yet billed; ticking one fills in its lines)</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {grns.map((g) => (
                <Form.Check key={g.id} type="checkbox" id={`ng-grn-${g.id}`}
                  label={`${g.grnNumber} (${formatDate(g.grnDate)}) - ${currency} ${num(g.value).toFixed(2)}`}
                  checked={selectedGrns.includes(g.id)} onChange={() => toggleGrn(g.id)} />
              ))}
            </div>
            <div className="text-muted small">Goods you linked are already on the vendor&apos;s account from the receipt, so only any difference is booked.</div>
          </div>
        )}

        <Table bordered size="sm" responsive>
          <thead>
            <tr>
              <th style={{ width: 120 }}>Type</th><th style={{ minWidth: 200 }}>Description</th><th style={{ width: 100 }}>Qty</th><th style={{ width: 110 }}>Unit price</th>
              <th style={{ width: 140 }}>Amount</th><th style={{ minWidth: 200 }}>Booked to</th><th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr key={idx}>
                <td>
                  <Form.Select size="sm" value={l.lineType} onChange={(e) => setLine(idx, e.target.value === 'GOODS'
                    ? { lineType: 'GOODS', expenseLedgerId: '' }
                    : { lineType: e.target.value, expenseLedgerId: '', itemId: null, unitOfMeasurementId: null, unitSymbol: '', quantity: '', unitPrice: '' })}>
                    <option value="GOODS">Goods</option>
                    <option value="SERVICE">Service</option>
                    <option value="EXPENSE">Expense</option>
                  </Form.Select>
                </td>
                <td>
                  {l.lineType === 'GOODS' ? (
                    <ItemDescriptionInput value={l.description} itemId={l.itemId} items={items}
                      onChange={(text) => setLine(idx, { description: text, itemId: null, unitOfMeasurementId: null, unitSymbol: '' })}
                      onPick={(item) => setLine(idx, {
                        description: item.name, itemId: item.itemId,
                        unitOfMeasurementId: item.unitOfMeasurementId, unitSymbol: item.unitSymbol || ''
                      })} />
                  ) : (
                    <Form.Control size="sm" value={l.description} maxLength={255} onChange={(e) => setLine(idx, { description: e.target.value })} />
                  )}
                </td>
                <td>
                  {l.lineType === 'GOODS'
                    ? (
                      <>
                        <Form.Control size="sm" type="number" min="0" step="any" value={l.quantity} onChange={(e) => editAmount(idx, 'quantity', e.target.value)} />
                        {l.unitSymbol && <div className="small text-muted">{l.unitSymbol}</div>}
                      </>
                    )
                    : <span className="text-muted">-</span>}
                </td>
                <td>
                  {l.lineType === 'GOODS'
                    ? <Form.Control size="sm" type="number" min="0" step="any" value={l.unitPrice} onChange={(e) => editAmount(idx, 'unitPrice', e.target.value)} />
                    : <span className="text-muted">-</span>}
                </td>
                <td><Form.Control size="sm" type="number" min="0" step="0.01" value={l.amount} onChange={(e) => editAmount(idx, 'amount', e.target.value)} /></td>
                <td>
                  {l.lineType === 'GOODS' ? (
                    <span className="text-muted small">Purchases</span>
                  ) : (
                    <Form.Select size="sm" value={l.expenseLedgerId} onChange={(e) => setLine(idx, { expenseLedgerId: e.target.value })}>
                      <option value="">Non-GST Services &amp; Expenses (default)</option>
                      {ledgers.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </Form.Select>
                  )}
                </td>
                <td>
                  {lines.length > 1 && (
                    <Button size="sm" variant="outline-danger" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}>&times;</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={() => setLines((prev) => [...prev, emptyLine()])}>+ Add line</Button>
        {goodsWithoutGrn && (
          <div className="mt-3">
            <GoodsReceiptChoice value={goodsReceipt} onChange={setGoodsReceipt} uninvoicedGrns={grns.length} document="purchase" idPrefix="ngp-goods" />
          </div>
        )}

        <Row className="g-3 mt-2">
          <Col md={7}>
            <Form.Group>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={5} className="text-end">
            <div className="fs-5">Total owed to the vendor: <strong>{currency} {round2(total).toFixed(2)}</strong></div>
            <div className="text-muted small">No GST and no input credit on this.</div>
            {selectedGrns.length > 0 && (
              <Alert variant={Math.abs(difference) < 0.005 ? 'success' : 'warning'} className="py-1 mt-2 mb-0 small text-start">
                Receipts already hold {currency} {round2(grnValue).toFixed(2)} on the vendor&apos;s account.
                {Math.abs(difference) < 0.005
                  ? ' The goods on the slip match, so nothing more is booked for them.'
                  : ` The goods on the slip are ${difference > 0 ? 'above' : 'below'} that by ${currency} ${Math.abs(difference).toFixed(2)}, and the account is adjusted by the same amount.`}
              </Alert>
            )}
          </Col>
        </Row>
        <Form.Group className="mt-3">
          <Form.Label>Photo of the slip (optional)</Form.Label>
          <Form.Control type="file" accept="image/*,application/pdf" multiple disabled={photos.length >= MAX_SLIP_FILES} onChange={addPhotos} />
          <Form.Text className="text-muted">
            Up to {MAX_SLIP_FILES}: a photo (the front and back, say) or a PDF. Photos are shrunk before they are uploaded.
          </Form.Text>
          {photos.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {photos.map((p, idx) => (
                <Badge key={idx} bg="light" text="dark" className="border d-flex align-items-center gap-2 p-2">
                  {p.name} ({Math.max(1, Math.round(p.size / 1024))} KB)
                  <Button size="sm" variant="link" className="p-0 text-danger" aria-label={`Remove ${p.name}`}
                    onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}>&times;</Button>
                </Badge>
              ))}
            </div>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={saving}>{saving ? 'Recording...' : 'Record purchase'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function SlipDetail({ slip, money, currency, onHide, onChanged }) {
  const { user } = useAuth();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [full, setFull] = useState(null);
  const [photos, setPhotos] = useState([]); // {id, fileName, pdf, url}
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoReload, setPhotoReload] = useState(0);
  const canRemovePhotos = (user?.roles || []).some(isOrgAdminRole) || hasAnyRole(user, ['PROC_MGR']);

  useEffect(() => {
    setCancelling(false);
    setReason('');
    setFull(null);
    if (!slip) return;
    axios.get(`/api/purchase-invoices/${slip.id}`).then((r) => setFull(r.data)).catch(() => setFull(slip));
  }, [slip]);

  // The photos are fetched with the sign-in header, so each is turned into a local address the browser can show
  useEffect(() => {
    if (!slip) { setPhotos([]); return undefined; }
    let cancelled = false;
    const urls = [];
    (async () => {
      try {
        const list = (await axios.get(`/api/purchase-invoices/${slip.id}/attachments`)).data || [];
        const loaded = [];
        for (const a of list) {
          const blob = (await axios.get(`/api/purchase-invoices/attachments/${a.id}/content`, { responseType: 'blob' })).data;
          const url = URL.createObjectURL(blob);
          urls.push(url);
          loaded.push({ id: a.id, fileName: a.fileName, pdf: a.pdf, url });
        }
        if (!cancelled) setPhotos(loaded);
      } catch (e) {
        if (!cancelled) setPhotos([]);
      }
    })();
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [slip, photoReload]);

  const addPhoto = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    setPhotoBusy(true);
    try {
      const ready = [];
      for (const f of picked.slice(0, MAX_SLIP_FILES - photos.length)) {
        try { ready.push(await prepareSlipFile(f)); } catch (err) { toast.error(err.message); }
      }
      if (ready.length > 0) {
        await uploadSlipFiles(axios, slip.id, ready, (m) => toast.error(m));
        setPhotoReload((k) => k + 1);
      }
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async (photo) => {
    if (!window.confirm(`Remove ${photo.fileName}?`)) return;
    try {
      await axios.delete(`/api/purchase-invoices/attachments/${photo.id}`);
      setPhotoReload((k) => k + 1);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not remove the photo'));
    }
  };

  const cancel = async () => {
    try {
      await axios.post(`/api/purchase-invoices/${slip.id}/cancel`, { reason });
      toast.success('Purchase cancelled and its entries reversed');
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to cancel the purchase'));
    }
  };

  const s = full || slip;
  return (
    <Modal show={Boolean(slip)} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{s ? `${s.internalNumber}${s.vendorInvoiceNumber ? ` - slip ${s.vendorInvoiceNumber}` : ''}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {s && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-1"><strong>Vendor:</strong> {s.vendorName}</p>
                <p className="mb-1"><strong>Slip date:</strong> {formatDate(s.invoiceDate)} <span className="text-muted">(booked {formatDate(s.bookingDate)})</span></p>
              </Col>
              <Col md={6}>
                <p className="mb-1"><strong>GRNs covered:</strong> {(s.grnNumbers || []).join(', ') || 'none'}<GoodsStatusBadge status={s.goodsStatus} /></p>
                {(s.grnNumbers || []).length > 0 && (
                  <p className="mb-1"><strong>Goods value already on account (GRNs):</strong> {money(s.grnValue)}</p>
                )}
                {s.status === 'CANCELLED' && <p className="mb-1 text-danger"><strong>Cancelled:</strong> {s.cancelReason}</p>}
              </Col>
            </Row>
            <Table bordered size="sm">
              <thead>
                <tr><th>Type</th><th>Description</th><th className="text-end">Qty</th><th className="text-end">Unit price</th><th className="text-end">Amount</th><th>Booked to</th></tr>
              </thead>
              <tbody>
                {(s.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td>{TYPE_LABEL[l.lineType] || l.lineType}</td>
                    <td>{l.description}</td>
                    <td className="text-end">{l.quantity != null ? `${Number(l.quantity)}${l.unitSymbol ? ` ${l.unitSymbol}` : ''}` : '-'}</td>
                    <td className="text-end">{l.quantity != null && Number(l.quantity) > 0 ? `${currency} ${rateText(l.taxableAmount / l.quantity)}` : '-'}</td>
                    <td className="text-end">{money(l.taxableAmount)}</td>
                    <td>{l.lineType === 'GOODS' ? 'Purchases' : (l.expenseLedgerName || '-')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="text-end">Total: <strong>{money(s.totalAmount)}</strong> <span className="text-muted">(no GST, no input credit)</span></div>
            <div className="mt-3">
              <strong>Photos of the slip</strong>
              <div className="d-flex flex-wrap gap-3 mt-2 align-items-start">
                {photos.map((p) => (
                  <div key={p.id} className="text-center" style={{ width: 120 }}>
                    <a href={p.url} target="_blank" rel="noreferrer" title={`Open ${p.fileName}`}>
                      {p.pdf
                        ? <div className="border rounded d-flex align-items-center justify-content-center" style={{ height: 90 }}>PDF</div>
                        : <img src={p.url} alt={p.fileName} className="border rounded" style={{ maxWidth: 120, maxHeight: 90 }} />}
                    </a>
                    <div className="small text-truncate" title={p.fileName}>{p.fileName}</div>
                    {s.status === 'POSTED' && canRemovePhotos && (
                      <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removePhoto(p)}>Remove</Button>
                    )}
                  </div>
                ))}
                {photos.length === 0 && <span className="text-muted small">None added.</span>}
              </div>
              {s.status === 'POSTED' && photos.length < MAX_SLIP_FILES && (
                <Form.Group className="mt-2" style={{ maxWidth: 380 }}>
                  <Form.Control size="sm" type="file" accept="image/*,application/pdf" multiple disabled={photoBusy} onChange={addPhoto} />
                  <Form.Text className="text-muted">{photoBusy ? 'Uploading...' : `Add a photo or PDF (up to ${MAX_SLIP_FILES} in all).`}</Form.Text>
                </Form.Group>
              )}
            </div>
            {s.notes && <p className="mt-2"><strong>Notes:</strong> {s.notes}</p>}
            {cancelling && (
              <Form.Group className="mt-3">
                <Form.Label>Reason for cancelling</Form.Label>
                <Form.Control value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                <Form.Text className="text-muted">
                  This reverses every entry the purchase made{['RECEIVED_NOW', 'ARRIVED'].includes(s.goodsStatus) ? ', and the GRN its goods came in on (refused if any of that stock has been used)' : ''}.
                </Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!cancelling && <GoodsToArriveButton bill={s} onChanged={onChanged} />}
        {s?.status === 'POSTED' && !cancelling && <Button variant="outline-danger" onClick={() => setCancelling(true)}>Cancel purchase</Button>}
        {cancelling && (
          <>
            <Button variant="secondary" onClick={() => setCancelling(false)}>Keep it</Button>
            <Button variant="danger" onClick={cancel} disabled={!reason.trim()}>Cancel purchase</Button>
          </>
        )}
        {!cancelling && <Button variant="secondary" onClick={onHide}>Close</Button>}
      </Modal.Footer>
    </Modal>
  );
}

export default NonGstPurchases;
